import { apiClient } from './client';

/**
 * 인사발령 API — backend `modules/hr/assignment.routes.ts` (인사, migration 130).
 *
 * ★ **등록은 «예정» 으로만 선다.** 사람의 소속·직위는 «시행» 을 눌러야 바뀐다 — 자동 시행이 없는
 *   것은 믿을 만한 스케줄러가 없어서다(라우트 머리말). 그래서 목록이 `overdue` 를 함께 준다.
 * ★ **옛 서버에는 이 라우트가 없다**(404). 부르는 쪽이 실패를 삼키고 화면을 접는다.
 */

export type AssignmentKind =
  | 'TRANSFER' | 'PROMOTION'
  // 직책 — 보임 · 겸직 · 보직해임 (migration 144, 2026-08-29). 직위(승진)와 **다른 축**이다.
  | 'DUTY' | 'CONCURRENT' | 'RELIEVE'
  | 'LEAVE_ABSENCE' | 'REINSTATE' | 'RESIGN';
export type AssignmentStatus = 'SCHEDULED' | 'APPLIED' | 'CANCELED';

export interface AssignmentSideDto {
  teamId: string | null;
  teamName: string | null;
  position: string | null;
  jobLevel: number | null;
  employmentStatus: string | null;
}

export interface AssignmentDto {
  id: string;
  userId: string;
  userName: string | null;
  kind: AssignmentKind;
  kindLabel: string;
  status: AssignmentStatus;
  /** 시행일이 지났는데 아직 예정 — 화면이 맨 앞에 세운다. */
  overdue: boolean;
  /**
   * 직책을 건드리는 발령인가 — 화면이 묻는 칸을 이 값으로 가른다.
   * ★ 2026-08-29 이전에는 `recordOnly`(담을 칸이 없어 이력으로만 남음)였다. 이제 보임은
   *   실제로 일어난다(`hr_user_job_roles`).
   */
  roleKind: boolean;
  effectiveDate: string;
  docNo: string | null;
  before: AssignmentSideDto;
  after: AssignmentSideDto;
  reason: string | null;
  appliedAt: string | null;
  createdAt: string;
}

export interface CreateAssignmentInput {
  userId: string;
  kind: AssignmentKind;
  effectiveDate: string;
  afterTeamId?: string | null;
  afterPosition?: string | null;
  afterJobLevel?: number | null;
  /** 보임 · 겸직 · 보직해임이 가리키는 직책. */
  afterJobRoleId?: string | null;
  /** 어느 조직에 대한 보임인가 — 직책의 «범위» 가 정한다(`TEAM`·`DIVISION`·`COMPANY`). */
  roleOrgKind?: string | null;
  roleOrgId?: string | null;
  reason?: string | null;
}

export async function listAssignments(params?: { userId?: string; status?: AssignmentStatus }): Promise<{
  assignments: AssignmentDto[]; overdue: number;
}> {
  const { data } = await apiClient.get<{ assignments: AssignmentDto[]; overdue: number }>(
    '/hr/assignments', { params },
  );
  return data;
}

/**
 * 한 사람의 발령 이력 — 담당자와 **본인**이 같은 라우트를 쓴다(시안 B2 「내 발령 이력」).
 *
 * ★ **시행된 것만** 온다. 예정·취소는 본인에게 «아직 아닌 일» 이라, 보여 주면 부서가 이미
 *   바뀐 줄 알고 움직인다 — 담당자 목록(`listAssignments`)은 전부 그대로 본다.
 * ★ 옛 서버에는 이 라우트가 없다(404). 부르는 쪽이 실패를 삼킨다.
 */
export async function listUserAssignments(userId: string): Promise<{ assignments: AssignmentDto[] }> {
  const { data } = await apiClient.get<{ assignments: AssignmentDto[] }>(`/hr/assignments/user/${userId}`);
  return data;
}

export async function createAssignment(input: CreateAssignmentInput): Promise<AssignmentDto> {
  const { data } = await apiClient.post<{ assignment: AssignmentDto }>('/hr/assignments', input);
  return data.assignment;
}

/** 시행 — **사람의 값이 여기서 바뀐다.** */
export async function applyAssignment(id: string): Promise<AssignmentDto> {
  const { data } = await apiClient.post<{ assignment: AssignmentDto }>(`/hr/assignments/${id}/apply`, {});
  return data.assignment;
}

/** 취소 — 시행 전에만. 시행된 것은 «되돌리는 발령» 을 새로 등록해야 한다(서버가 409). */
export async function cancelAssignment(id: string): Promise<AssignmentDto> {
  const { data } = await apiClient.post<{ assignment: AssignmentDto }>(`/hr/assignments/${id}/cancel`, {});
  return data.assignment;
}
